"use client";

import { useMemo, useState } from "react";

import { useHiddenJobs } from "@/features/jobs/hooks/use-hidden-jobs";
import { useJobSearch } from "@/features/jobs/hooks/use-jobs";
import type {
  JobSearchParams,
  JobSortField,
  JobSortOrder,
} from "@/features/jobs/types";

/** Sort fields + labels, ported from the legacy `/jobs` "Sort by" select. */
export const SORT_OPTIONS: { value: JobSortField; label: string }[] = [
  { value: "added", label: "Newest" },
  { value: "duration", label: "Duration" },
  { value: "positions", label: "Positions" },
  { value: "job_done", label: "Popularity" },
  { value: "job_price", label: "Most paying" },
];

export const ORDER_OPTIONS: { value: JobSortOrder; label: string }[] = [
  { value: "desc", label: "Descending" },
  { value: "asc", label: "Ascending" },
];

/** Page sizes, matching the legacy "View" select (`all` = every result). */
export const LIMIT_OPTIONS: (number | "all")[] = [10, 20, 50, 100, "all"];

export type JobsTab = "all" | "stats";

/**
 * Every bit of `/jobs` browse behaviour: tab, sort/order/page-size state,
 * the query, the derived "Showing X - Y of Z" counts, pagination, and the
 * hide-job control.
 *
 * The four design variants share this hook, so they cannot drift apart
 * behaviourally — a variant is a pure presentation of this return value. Once
 * one is locked in, this stays and the variants collapse into it.
 */
export function useJobsBrowse() {
  const [tab, setTab] = useState<JobsTab>("all");
  const [params, setParams] = useState<JobSearchParams>({
    page: 1,
    limit: 10,
    sortBy: "added",
    orientation: "desc",
  });

  const query = useJobSearch(params);
  const hiddenJobs = useHiddenJobs();

  /** Any sort/size change resets to page 1 — page 4 of the old order is
   * meaningless under a new one. */
  const update = (patch: Partial<JobSearchParams>) =>
    setParams((prev) => ({ ...prev, page: 1, ...patch }));

  const setPage = (page: number) => setParams((prev) => ({ ...prev, page }));

  const result = query.data;
  const showingAll = params.limit === "all";
  const total = result?.totalCount ?? 0;

  const pageSize =
    (showingAll
      ? total
      : typeof params.limit === "number"
        ? params.limit
        : 0) || 1;

  // `POST job/search` returns ONLY `{ Jobs, totalCount }` — there is no
  // `totalPages` in the payload. Reading `result.totalPages` therefore pinned
  // the count at 0 forever, and `/jobs` gated its pager on `totalPages > 0`, so
  // the controls never rendered and every result set was stranded on page 1.
  // Legacy derives it client-side for the same reason
  // (`count={Math.ceil(totalCount / limit)}` in `containers/jobs/jobs.tsx`), so
  // compute it here and defer to a server-sent value only if one ever appears.
  const totalPages =
    result?.totalPages ||
    (total === 0 ? 0 : showingAll ? 1 : Math.ceil(total / pageSize));

  // Guard against a stale page surviving a shrinking result set (page 5 of a
  // 50-job list, then the list drops to 20). Only clamps once a count is known,
  // so an in-flight page change isn't yanked back to page 1.
  const page = totalPages > 0 ? Math.min(params.page, totalPages) : params.page;

  // The counts describe the SERVER page, not the post-hide list: hiding is a
  // local preference, and "Showing 1 - 9 of 40" after hiding one row would read
  // as the backend having lost a job.
  //
  // `to` counts the rows the server actually sent rather than assuming a full
  // page, because it often doesn't send one: `{limit:50}` returns 44 rows with
  // `totalCount:50`, and `{limit:"all"}` returns 10. Deriving the end index
  // from `total` claimed "Showing 1–50 of 50" over 44 cards — the six missing
  // jobs simply vanished. (Both are backend quirks; this keeps the label
  // truthful about what is on screen either way.)
  const serverRows = result?.jobs?.length ?? 0;
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = total === 0 ? 0 : from + serverRows - 1;

  const jobs = useMemo(() => {
    const all = result?.jobs ?? [];
    if (!hiddenJobs.ready || hiddenJobs.hiddenCount === 0) return all;
    return all.filter((j) => !hiddenJobs.hiddenIds.includes(j.jobId));
  }, [
    result?.jobs,
    hiddenJobs.ready,
    hiddenJobs.hiddenCount,
    hiddenJobs.hiddenIds,
  ]);

  /** Hidden rows on THIS page — drives the "N hidden · Show all" affordance. */
  const hiddenHere = (result?.jobs?.length ?? 0) - jobs.length;

  return {
    tab,
    setTab,
    params,
    update,
    setPage,
    query,
    jobs,
    total,
    totalPages,
    /** Clamped current page — what the pager and the counts must render. */
    page,
    from,
    to,
    showingAll,
    descending: params.orientation !== "asc",
    hide: hiddenJobs.hide,
    showAll: hiddenJobs.showAll,
    hiddenHere,
    hiddenCount: hiddenJobs.hiddenCount,
  };
}
